import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import axios from '../api/axios';
import Header from '../layouts/Header';

const fmt = (val, dec = 2) =>
    `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const fmtNum = (val) => Number(val || 0).toLocaleString('en-IN');
const fmtTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};
const fmtDateLabel = (df, cr) => {
    if (df === 'custom' && cr) return `${cr.start} → ${cr.end}`;
    return { today: 'Today', yesterday: 'Yesterday', last7days: 'Last 7 Days', last30days: 'Last 30 Days', thisMonth: 'This Month', lastMonth: 'Last Month' }[df] || df;
};

const KpiPill = ({ label, value, icon, color, bg, sub }) => (
    <div style={{ background: bg, borderRadius: 10, padding: '10px 13px', borderLeft: `3px solid ${color}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
            <i className={`bi ${icon}`} style={{ fontSize: 12, color, opacity: 0.75 }} />
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', letterSpacing: '-0.4px', lineHeight: 1.1 }}>{value}</div>
        {sub && <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 1 }}>{sub}</div>}
    </div>
);

const Card = ({ title, icon, children, style = {} }) => (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', ...style }}>
        {title && (
            <div style={{ padding: '9px 14px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 7, background: '#fafafa' }}>
                <i className={`bi ${icon}`} style={{ fontSize: 11, color: '#4d8af0' }} />
                <span style={{ fontSize: 10, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.7px' }}>{title}</span>
            </div>
        )}
        <div style={{ padding: '11px 13px' }}>{children}</div>
    </div>
);

const RankRow = ({ rank, name, qty, isTop }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 7, marginBottom: 3, background: rank === 1 ? (isTop ? '#f0fdf4' : '#fff5f5') : 'transparent' }}>
        <div style={{ width: 18, height: 18, borderRadius: '50%', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: rank === 1 ? (isTop ? '#22c55e' : '#ef4444') : '#e5e7eb', color: rank === 1 ? '#fff' : '#9ca3af' }}>{rank}</div>
        <span style={{ flex: 1, fontSize: 11, color: '#374151', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {!name || name === 'N/A' ? <span style={{ color: '#d1d5db' }}>—</span> : name}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99, flexShrink: 0, color: isTop ? '#16a34a' : '#dc2626', background: isTop ? '#dcfce7' : '#fee2e2' }}>{qty}</span>
    </div>
);

const HourlyBar = ({ slot, orders, sales, maxOrders }) => {
    const pct = maxOrders > 0 ? (orders / maxOrders) * 100 : 0;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
            <span style={{ width: 110, fontSize: 10, color: '#6b7280', flexShrink: 0 }}>{slot}</span>
            <div style={{ flex: 1, height: 6, background: '#f3f4f6', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg,#4d8af0,#6fa3f7)', borderRadius: 99 }} />
            </div>
            <span style={{ width: 52, fontSize: 10, fontWeight: 600, color: '#374151', textAlign: 'right', flexShrink: 0 }}>{orders} orders</span>
            <span style={{ width: 72, fontSize: 10, fontWeight: 600, color: '#059669', textAlign: 'right', flexShrink: 0 }}>{fmt(sales)}</span>
        </div>
    );
};

const CouponRow = ({ coupon, used, discount }) => (
    <div style={{ display: 'flex', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #f9fafb' }}>
        <i className="bi bi-tag-fill" style={{ fontSize: 10, color: '#8b5cf6', marginRight: 7, flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#374151', fontFamily: 'monospace' }}>{coupon}</span>
        <span style={{ fontSize: 10, color: '#6b7280', marginRight: 10 }}>{used}x used</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>-{fmt(discount)}</span>
    </div>
);

const OutletDetailScreen = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const { outletId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem('authToken');
    const { outletName = 'Outlet Details', dateFilter = 'today', customRange = null } = location.state || {};

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDetails = useCallback(async () => {
        try {
            setLoading(true); setError(null);
            let url = `/dashboard/getRevenueStatThroughSummaryByOutlet?outletId=${outletId}`;
            url += dateFilter === 'custom' && customRange
                ? `&filterType=custom&startDate=${customRange.start}&endDate=${customRange.end}`
                : `&filterType=${dateFilter}`;
            const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            setData(res?.data?.data || null);
        } catch (err) {
            console.error(err);
            setError('Failed to load outlet details.');
        } finally {
            setLoading(false);
        }
    }, [outletId, dateFilter, customRange, token]);

    useEffect(() => { fetchDetails(); }, [fetchDetails]);

    const maxOrders = useMemo(() =>
        Math.max(...(data?.hourlyDistribution || []).map(h => h.totalOrders || 0), 1),
        [data]);

    useEffect(() => {
        const s = document.createElement('style');
        s.id = 'outlet-print-style';
        s.innerHTML = `@media print { body > *:not(#outlet-detail-root) { display:none!important; } #outlet-detail-root { display:block!important; background:#fff!important; } .no-print { display:none!important; } @page { margin:15mm; } }`;
        document.head.appendChild(s);
        return () => document.getElementById('outlet-print-style')?.remove();
    }, []);

    const { stats, hourlyDistribution, topProducts, bottomProducts,
        topCoupons, totalOnlineSales, outletShiftSummary,
        pettyCashSummary, damagedProductsSummary, exhaustedStockSummary, userStatsSummary } = data || {};

    const netOrders = (Number(stats?.totalOrders) || 0) - (Number(stats?.cancelledOrders) || 0);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div id="outlet-detail-root" className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                {/* Top Bar */}
                <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button onClick={() => navigate(-1)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                            <i className="bi bi-arrow-left" /> Back
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 9, background: '#4d8af0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="bi bi-shop" style={{ fontSize: 16, color: '#fff' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{outletName}</div>
                                <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <i className="bi bi-calendar3" style={{ fontSize: 10 }} /> {fmtDateLabel(dateFilter, customRange)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <button onClick={() => window.print()}
                        style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#4d8af0', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600, boxShadow: '0 2px 8px rgba(77,138,240,0.3)' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#3b7de8'}
                        onMouseLeave={e => e.currentTarget.style.background = '#4d8af0'}
                    >
                        <i className="bi bi-file-earmark-pdf-fill" style={{ fontSize: 14 }} /> Download PDF
                    </button>
                </div>

                {loading && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 340, gap: 12 }}>
                        <Spinner animation="border" variant="primary" />
                        <span style={{ fontSize: 13, color: '#6b7280' }}>Loading outlet details...</span>
                    </div>
                )}
                {error && (
                    <div style={{ textAlign: 'center', padding: '80px 0' }}>
                        <i className="bi bi-exclamation-circle" style={{ fontSize: 36, color: '#ef4444', display: 'block', marginBottom: 10 }} />
                        <div style={{ fontSize: 13, color: '#374151', marginBottom: 14 }}>{error}</div>
                        <button onClick={fetchDetails} style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: '#4d8af0', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Retry</button>
                    </div>
                )}

                {!loading && !error && data && (
                    <>
                        {/* Hero Strip */}
                        <div style={{
                            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5fa3 60%, #4d8af0 100%)',
                            borderRadius: 14, padding: '14px 4px', marginBottom: 10,
                            display: 'grid', gridTemplateColumns: 'repeat(6,1fr)',
                            position: 'relative', overflow: 'hidden',
                        }}>
                            <div style={{ position: 'absolute', right: -40, top: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
                            {[
                                { label: 'Total Sales', value: fmt(stats?.totalSales), icon: 'bi-currency-rupee' },
                                { label: 'Net Sales', value: fmt(stats?.netSales), icon: 'bi-receipt' },
                                { label: 'App Net Sale', value: fmt(stats?.appNetSale), icon: 'bi-phone' },
                                { label: 'Kiosk Net Sale', value: fmt(stats?.kioskNetSale), icon: 'bi-display' },
                                { label: 'Net Orders', value: fmtNum(netOrders), icon: 'bi-bag-check' },
                                { label: 'Total Online', value: fmt(totalOnlineSales?.totalRevenue), icon: 'bi-globe' },
                            ].map((item, i) => (
                                <div key={i} style={{ padding: '6px 16px', borderRight: i < 5 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                                    <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <i className={`bi ${item.icon}`} style={{ fontSize: 10 }} /> {item.label}
                                    </div>
                                    <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>{item.value}</div>
                                </div>
                            ))}
                        </div>

                        {/* Order Pills Row */}
                        <div className='mt-3' style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 8, marginBottom: 10 }}>
                            <KpiPill label="Total Orders" value={fmtNum(stats?.totalOrders)} icon="bi-bag" color="#0ea5e9" bg="#f0f9ff" />
                            <KpiPill label="Cancelled" value={fmtNum(stats?.cancelledOrders)} sub={stats?.cancelledOrderAmount ? `${fmt(stats.cancelledOrderAmount)} lost` : 'No cancellations'} icon="bi-x-circle" color="#ef4444" bg="#fff5f5" />
                            <KpiPill label="Delayed" value={fmtNum(stats?.delayedOrders)} icon="bi-clock-history" color="#f59e0b" bg="#fffbeb" />
                            <KpiPill label="Early" value={fmtNum(stats?.earlyOrders)} icon="bi-lightning-charge" color="#22c55e" bg="#f0fdf4" />
                            <KpiPill label="Complimentary" value={fmtNum(stats?.complimentaryOrders)} icon="bi-gift" color="#8b5cf6" bg="#f5f3ff" />
                            <KpiPill label="Shift Open" value={fmtTime(outletShiftSummary?.openTime)} sub={outletShiftSummary?.closeTime ? `Closed ${fmtTime(outletShiftSummary.closeTime)}` : 'Still open'} icon="bi-door-open" color="#0ea5e9" bg="#f0f9ff" />
                            <KpiPill label="New Users" value={fmtNum(userStatsSummary?.newUserJoinedCount)} sub={`${fmtNum(userStatsSummary?.repeatedUserCount)} returning`} icon="bi-people-fill" color="#8b5cf6" bg="#f5f3ff" />
                        </div>

                        {/* Bento Grid */}
                        <div className="bento-grid mt-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, alignItems: 'start' }}>

                            {/* Col A */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <Card title="Online Platform Sales" icon="bi-globe2">
                                    {[
                                        { label: 'Swiggy', icon: 'bi-bag-heart', color: '#ff6900', bg: '#fff4ed', rev: totalOnlineSales?.swiggy?.revenue, orders: totalOnlineSales?.swiggy?.totalOrders },
                                        { label: 'Zomato', icon: 'bi-fire', color: '#e23744', bg: '#fff5f5', rev: totalOnlineSales?.zomato?.revenue, orders: totalOnlineSales?.zomato?.totalOrders },
                                    ].map(p => (
                                        <div key={p.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, background: p.bg, marginBottom: 6 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <i className={`bi ${p.icon}`} style={{ fontSize: 14, color: p.color }} />
                                                <div>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>{p.label}</div>
                                                    <div style={{ fontSize: 10, color: '#9ca3af' }}>{fmtNum(p.orders)} orders</div>
                                                </div>
                                            </div>
                                            <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{fmt(p.rev)}</div>
                                        </div>
                                    ))}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 4px', borderTop: '1px dashed #e5e7eb', marginTop: 2 }}>
                                        <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Total</span>
                                        <span style={{ fontSize: 14, fontWeight: 800, color: '#0ea5e9' }}>{fmt(totalOnlineSales?.totalRevenue)}</span>
                                    </div>
                                </Card>

                                <Card title="Petty Cash" icon="bi-cash-stack">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                                        {[
                                            { label: 'Credit', value: fmt(pettyCashSummary?.totalCreditAmount), color: '#22c55e' },
                                            { label: 'Debit', value: fmt(pettyCashSummary?.totalDebitAmount), color: '#ef4444' },
                                            { label: 'Net', value: fmt(pettyCashSummary?.netExpense), color: '#f59e0b' },
                                        ].map(c => (
                                            <div key={c.label} style={{ textAlign: 'center', padding: '8px 4px', background: '#f9fafb', borderRadius: 8 }}>
                                                <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>{c.label}</div>
                                                <div style={{ fontSize: 12, fontWeight: 800, color: c.color, marginTop: 2 }}>{c.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </Card>

                                <Card title="Stock Alerts" icon="bi-exclamation-triangle-fill">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                        <div style={{ textAlign: 'center', padding: '10px 4px', background: '#fff5f5', borderRadius: 8 }}>
                                            <div style={{ fontSize: 22, fontWeight: 800, color: '#ef4444' }}>{fmtNum(exhaustedStockSummary?.totalExhaustedEntries)}</div>
                                            <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Exhausted</div>
                                        </div>
                                        <div style={{ textAlign: 'center', padding: '10px 4px', background: '#fffbeb', borderRadius: 8 }}>
                                            <div style={{ fontSize: 22, fontWeight: 800, color: '#f59e0b' }}>{fmtNum(damagedProductsSummary?.totalDamagedProducts)}</div>
                                            <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Damaged</div>
                                        </div>
                                    </div>
                                </Card>

                                <Card title="Raw Material" icon="bi-boxes">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                        <div style={{ textAlign: 'center', padding: '10px 4px', background: '#f0f9ff', borderRadius: 8 }}>
                                            <div style={{ fontSize: 22, fontWeight: 800, color: '#0ea5e9' }}>{fmtNum(data?.rawMaterialSummary?.totalTransactions)}</div>
                                            <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Transactions</div>
                                        </div>
                                        <div style={{ textAlign: 'center', padding: '10px 4px', background: '#fff5f5', borderRadius: 8 }}>
                                            <div style={{ fontSize: 22, fontWeight: 800, color: '#ef4444' }}>{fmtNum(data?.rawMaterialSummary?.totalDamagedRawMaterialQuantity)}</div>
                                            <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Damaged Qty</div>
                                        </div>
                                    </div>
                                </Card>
                            </div>

                            {/* Col B */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <Card title="Hourly Distribution" icon="bi-clock-fill">
                                    {hourlyDistribution?.length > 0 ? (
                                        <>
                                            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <span style={{ width: 110, fontSize: 9, color: '#d1d5db', textTransform: 'uppercase', fontWeight: 700 }}>Slot</span>
                                                <span style={{ flex: 1 }} />
                                                <span style={{ width: 52, fontSize: 9, color: '#d1d5db', textAlign: 'right', fontWeight: 700 }}>Orders</span>
                                                <span style={{ width: 72, fontSize: 9, color: '#d1d5db', textAlign: 'right', fontWeight: 700 }}>Sales</span>
                                            </div>
                                            {hourlyDistribution.map((h, i) => (
                                                <HourlyBar key={i} slot={h.timeSlot} orders={h.totalOrders} sales={h.netSales} maxOrders={maxOrders} />
                                            ))}
                                        </>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '20px 0', color: '#d1d5db', fontSize: 12 }}>No hourly data</div>
                                    )}
                                </Card>

                                {topCoupons?.length > 0 && (
                                    <Card title="Top Coupons Used" icon="bi-tag-fill">
                                        {topCoupons.map((c) => (
                                            <CouponRow key={c._id} coupon={c.couponCode} used={c.totalUsed} discount={c.totalDiscount} />
                                        ))}
                                    </Card>
                                )}
                            </div>

                            {/* Col C */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <Card title="Top Performers" icon="bi-trophy-fill">
                                    {(topProducts || []).length > 0
                                        ? topProducts.map((p, i) => <RankRow key={p._id} rank={i + 1} name={p.productName} qty={p.totalQty} isTop={true} />)
                                        : <div style={{ fontSize: 11, color: '#d1d5db', padding: '8px 0' }}>No data</div>
                                    }
                                </Card>
                                <Card title="Low Performers" icon="bi-arrow-down-circle-fill">
                                    {(bottomProducts || []).length > 0
                                        ? bottomProducts.map((p, i) => <RankRow key={p._id} rank={i + 1} name={p.productName} qty={p.totalQty} isTop={false} />)
                                        : <div style={{ fontSize: 11, color: '#d1d5db', padding: '8px 0' }}>No data</div>
                                    }
                                </Card>
                            </div>
                        </div>
                    </>
                )}

                <style>{`
                    @media (max-width: 960px) { .bento-grid { grid-template-columns: 1fr 1fr !important; } }
                    @media (max-width: 620px) { .bento-grid { grid-template-columns: 1fr !important; } }
                `}</style>
            </div>
        </React.Fragment>
    );
};

export default OutletDetailScreen;